/**
 * InfilesHeroIllustration.tsx — hero-illustrasjon for /infiles
 *
 * Tre isometriske plattformer koblet med prikkede linjer: original-fil (grå
 * dokumentstabel) → ECU-brikke → modifisert fil (blå dokumentstabel med
 * sjekkmerke). Samme stil og temafarger som webhotell-illustrasjonen
 * (components/product/illustrations.module.css); kun blå og grå aksenter.
 */

import { useId } from 'react';
import { Box, iso } from '@/components/product/iso';
import styles from '@/components/product/illustrations.module.css';
import { Chip, DocStack } from './parts';

export default function InfilesHeroIllustration({ label }: { label: string }) {
  const uid = useId();
  const blue = `${uid}-blue`;

  const A = { x: -2.5, y: 0.5 }; // original
  const B = { x: -2.5, y: -5.5 }; // ECU
  const C = { x: 3.5, y: -3.5 }; // modifisert
  const size = 5;

  const [badgeX, badgeY] = iso(C.x + size / 2, C.y + size / 2, 2.7).split(',').map(Number);

  return (
    <svg className={styles.scene} viewBox="110 60 660 400" role="img" aria-label={label}>
      <defs>
        <linearGradient id={blue} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4589ff" />
          <stop offset="1" stopColor="#0043ce" />
        </linearGradient>
      </defs>

      {/* Prikkede forbindelser: original → ECU → modifisert */}
      <polyline className={styles.link} points={`${iso(A.x + 2, A.y, 0)} ${iso(B.x + 2.5, B.y + size, 0)}`} />
      <polyline className={styles.link} points={`${iso(B.x + size, B.y + 3, 0)} ${iso(C.x + 0.5, C.y, 0)}`} />
      <polyline className={styles.link} points={`${iso(A.x + size, A.y + 2, 0)} ${iso(C.x, C.y + 4.5, 0)}`} />

      {/* Bakerst: ECU-brikke */}
      <Box x={B.x} y={B.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <Chip x={B.x + 1.3} y={B.y + 1.3} z={0.3} />

      {/* Høyre: modifisert fil */}
      <Box x={C.x} y={C.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <DocStack x={C.x + 1.4} y={C.y + 1} z={0.3} sheets={4} modified />
      <g transform={`translate(${badgeX} ${badgeY})`}>
        <circle r="24" fill={`url(#${blue})`} />
        <polyline points="-11,0 -3,8 11,-8" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* Foran: original-fil */}
      <Box x={A.x} y={A.y} z={0} w={size} d={size} h={0.3} top={styles.top} left={styles.left} right={styles.right} />
      <DocStack x={A.x + 1.4} y={A.y + 1} z={0.3} sheets={4} />
    </svg>
  );
}
